// src/layouts/Layout.tsx
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/tooltip';

import {
    LayoutDashboard,
    Package,
    ShoppingCart,
    Wallet as WalletIcon,
    Award,
    Phone,
    Menu,
    LogOut,
    FileArchive,
    Home,
    ChevronsLeft,
    ChevronsRight,
} from 'lucide-react';

import { NotificationsBell } from '../components/NotificationsBell';
import { agentNotificationsAPI } from '../services/api';

interface LayoutProps {
    children: React.ReactNode;
}

const fa = new Intl.NumberFormat('fa-IR');

type NavKey =
    | 'dashboard'
    | 'products'
    | 'orders'
    | 'wallet'
    | 'licenses'
    | 'invoices'
    | 'support';

type UnreadSummary = {
    total?: number;
    orders?: number;
    invoices?: number;
    wallet?: number;
    licenses?: number;
    products?: number;
    support?: number;
    dashboard?: number;
};

const navigation: { name: string; href: string; icon: any; key: NavKey }[] = [
    { name: 'داشبورد', href: '/dashboard', icon: LayoutDashboard, key: 'dashboard' },
    { name: 'محصولات', href: '/products', icon: Package, key: 'products' },
    { name: 'سفارشات', href: '/orders', icon: ShoppingCart, key: 'orders' },
    { name: 'کیف پول', href: '/wallet', icon: WalletIcon, key: 'wallet' },
    { name: 'لایسنس‌ها', href: '/licenses', icon: Award, key: 'licenses' },
    { name: 'فاکتورها', href: '/invoices', icon: FileArchive, key: 'invoices' },
    { name: 'پشتیبانی', href: '/support', icon: Phone, key: 'support' },
];

const navKeyToGroup: Partial<Record<NavKey, keyof UnreadSummary>> = {
    dashboard: 'dashboard',
    products: 'products',
    orders: 'orders',
    wallet: 'wallet',
    licenses: 'licenses',
    invoices: 'invoices',
    support: 'support',
};

/**
 * مچ‌کردن مسیر فعلی به آیتم منو
 */
const getCurrentNav = (pathname: string) => {
    const clean = pathname.split('?')[0];
    const sorted = [...navigation].sort((a, b) => b.href.length - a.href.length);
    return (
        sorted.find(
            (item) =>
                clean === item.href || clean.startsWith(item.href + '/')
        ) || null
    );
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
    const { agent, logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [unread, setUnread] = useState<UnreadSummary>({});

    const currentNav = getCurrentNav(location.pathname);

    // --- unread counter ---
    useEffect(() => {
        let timer: number | undefined;

        const fetchUnread = async () => {
            try {
                const res = await agentNotificationsAPI.getUnreadSummary();
                if (res?.success && res.data) {
                    setUnread(res.data);
                }
            } catch {
                // ignore
            }
        };

        fetchUnread();
        timer = window.setInterval(fetchUnread, 10000);

        return () => timer && clearInterval(timer);
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleNavClick = async (key: NavKey, mobile: boolean) => {
        try {
            const group = navKeyToGroup[key];
            if (!group) {
                if (mobile) setSidebarOpen(false);
                return;
            }

            const groupCount = (unread[group] || 0) as number;

            if (groupCount > 0) {
                await agentNotificationsAPI.markReadGroup(group);

                // optimistic update
                setUnread(prev => {
                    const totalCount = prev.total || 0;
                    const newTotal = Math.max(0, totalCount - groupCount);

                    return {
                        ...prev,
                        [group]: 0,
                        total: newTotal,
                    };
                });
            }
        } catch (e) {
            console.error('markReadGroup error', e);
        } finally {
            if (mobile) setSidebarOpen(false);
        }
    };

    // -------- Sidebar --------
    const Sidebar = ({ mobile = false }: { mobile?: boolean }) => (
        <div
            className={[
                'flex flex-col h-full text-white shadow-2xl transition-all duration-300',
                'bg-gradient-to-b from-green-800 via-green-900 to-emerald-900',
                mobile ? 'w-full' : collapsed ? 'w-16' : 'w-64',
            ].join(' ')}
            onDoubleClick={() => !mobile && setCollapsed(c => !c)}
        >
            <div
                className="flex items-center h-16 px-3 border-b border-green-700 bg-gradient-to-r from-green-700 to-emerald-700 select-none"
                onDoubleClick={() => !mobile && setCollapsed(c => !c)}
                title="برای جمع/باز کردن دوبار کلیک کنید"
            >
                <Home className="h-6 w-6 text-green-200 flex-shrink-0" />
                {!collapsed && (
                    <h1 className="text-lg font-bold text-white mr-2">پنل نمایندگان</h1>
                )}

                {!mobile && (
                    <button
                        className="ml-auto text-green-200 hover:text-white transition"
                        onClick={(e) => {
                            e.stopPropagation();
                            setCollapsed(c => !c);
                        }}
                    >
                        {collapsed ? <ChevronsRight /> : <ChevronsLeft />}
                    </button>
                )}
            </div>

            <nav className="flex-1 px-2 py-4 space-y-1">
                {navigation.map(item => {
                    const Icon = item.icon;
                    const isActive = currentNav?.key === item.key;
                    const count = unread[item.key as keyof UnreadSummary] || 0;

                    const linkEl = (
                        <Link
                            key={item.href}
                            to={item.href}
                            className={[
                                'flex items-center rounded-xl transition-all duration-300 hover:scale-[1.02]',
                                isActive
                                    ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg border border-green-500'
                                    : 'text-green-100 hover:bg-gradient-to-r hover:from-green-700 hover:to-emerald-700 hover:text-white',
                                collapsed ? 'px-3 py-3 justify-center' : 'px-4 py-3',
                            ].join(' ')}
                            onClick={() => handleNavClick(item.key, mobile)}
                        >
                            <div className="relative flex-shrink-0">
                                <Icon className={collapsed ? 'w-5 h-5' : 'w-5 h-5 ml-3'} />
                                {collapsed && count > 0 && (
                                    <span className="absolute -top-1 -left-1 h-3 w-3 rounded-full bg-red-500 border border-green-900" />
                                )}
                            </div>

                            {!collapsed && (
                                <>
                                    <span className="truncate">{item.name}</span>
                                    {count > 0 && (
                                        <span className="mr-auto inline-flex items-center justify-center min-w-[1.75rem] px-1.5 py-0.5 rounded-full text-[11px] font-bold bg-red-500 text-white">
                                            {fa.format(count)}
                                        </span>
                                    )}
                                </>
                            )}
                        </Link>
                    );

                    return collapsed ? (
                        <Tooltip key={item.key}>
                            <TooltipTrigger asChild>{linkEl}</TooltipTrigger>
                            <TooltipContent side="left" className="bg-green-800 text-white border-green-700">
                                <span>{item.name}</span>
                            </TooltipContent>
                        </Tooltip>
                    ) : (
                        linkEl
                    );
                })}
            </nav>

            <div className="p-3 border-t border-green-700">
                <div className={['flex items-center ', collapsed ? 'justify-center' : 'space-x-3 space-x-reverse'].join(' ')}>
                    <Avatar className="h-9 w-9 border-2 bg-emerald-600 border-green-600">
                        <AvatarImage src={agent?.avatar_url} />
                        <AvatarFallback className={'bg-gradient-to-br from-teal-700 via-green-500 to-emerald-800'}>
                            {agent?.first_name?.[0]}
                        </AvatarFallback>
                    </Avatar>

                    {!collapsed && (
                        <div>
                            <p className="text-sm font-medium text-white">
                                {agent?.first_name} {agent?.last_name}
                            </p>
                            <p className="text-xs text-green-300">{agent?.phone_number}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    // -------- Layout --------
    return (
        <div className="flex h-screen bg-gradient-to-br from-green-50 to-emerald-50" dir="rtl">
            {/* Desktop */}
            <div className="hidden md:flex">
                <Sidebar />
            </div>

            {/* Mobile */}
            <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
                <SheetContent side="right" className="p-0 w-80 bg-transparent border-0">
                    <Sidebar mobile />
                </SheetContent>
            </Sheet>

            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Header */}
                <header className="relative z-40 bg-white/80 backdrop-blur-sm border-b border-green-200 px-4 py-3 shadow-sm">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 space-x-reverse">
                            <Button
                                variant="ghost"
                                size="icon"
                                className="md:hidden text-green-700 hover:bg-green-100 rounded-xl"
                                onClick={() => setSidebarOpen(true)}
                            >
                                <Menu />
                            </Button>

                            <h2 className="text-lg font-semibold text-green-900 hidden md:block">
                                {currentNav?.name || 'داشبورد'}
                            </h2>
                        </div>

                        <div className="flex items-center space-x-4 space-x-reverse">
                            <NotificationsBell totalUnread={unread.total || 0} />

                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="h-10 w-10 rounded-full hover:bg-green-100">
                                        <Avatar className="h-10 w-10 border-2 border-green-300">
                                            <AvatarImage src={agent?.avatar_url} />
                                            <AvatarFallback className={'bg-gradient-to-br from-teal-700 text-white via-green-500 to-emerald-800'}>
                                                {agent?.first_name?.[0]}
                                            </AvatarFallback>
                                        </Avatar>
                                    </Button>
                                </DropdownMenuTrigger>

                                <DropdownMenuContent align="end" className="w-56 bg-white border-green-200">
                                    <DropdownMenuLabel>
                                        <p className="font-medium">
                                            {agent?.first_name} {agent?.last_name}
                                        </p>
                                        <p className="text-xs text-green-600">{agent?.phone_number}</p>
                                    </DropdownMenuLabel>

                                    <DropdownMenuSeparator />

                                    <DropdownMenuItem
                                        className="text-red-600 hover:bg-red-50"
                                        onClick={handleLogout}
                                    >
                                        <LogOut className="ml-2 h-4 w-4" />
                                        خروج
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                </header>

                {/* Content */}
                <main className="flex-1 overflow-y-auto">
                    {children}
                </main>
            </div>
        </div>
    );
};

export default Layout;
